<template>
  <div class="learning-root">
    <p class="title is-4">Обучение</p>

    <div v-if="loading" class="notification is-light">
      Загрузка…
    </div>

    <template v-else>
      <p class="subtitle is-6">Текущее обучение</p>

      <div v-if="activeLearning" class="box">
        <div class="content">
          <p>
            <strong>{{ activeLearning.skill.name }}</strong>
            <span class="tag is-info is-light is-rounded ml-2">{{ localCompletionRate }}%</span>
          </p>
          <progress
            class="progress is-small is-info"
            :value="localCompletionRate"
            max="100"
          />
          <p class="has-text-grey is-size-7">
            Осталось: {{ formatRemaining(remainingSeconds) }}
          </p>
        </div>
      </div>

      <div v-else class="notification is-light">
        Нет активных обучений.
      </div>

      <p class="subtitle is-6 mt-4">Доступные навыки</p>

      <div v-if="availableSkills.length === 0" class="notification is-light">
        Нет навыков, доступных для изучения.
      </div>

      <div
        v-for="skill in availableSkills"
        :key="skill.id"
        class="box"
      >
        <div class="content">
          <p>
            <strong>{{ skill.name }}</strong>
            <span class="tag is-light is-rounded ml-2">{{ formatDuration(skill.duration) }}</span>
          </p>
          <p v-if="skill.description" class="has-text-grey">
            {{ skill.description }}
          </p>
          <p v-if="skill.skill_requirements && skill.skill_requirements.length > 0" class="is-size-7">
            <strong>Требования:</strong>
            <span
              v-for="req in skill.skill_requirements"
              :key="req.id"
              class="tag is-rounded ml-1"
              :class="isRequirementMet(req) ? 'is-success is-light' : 'is-danger is-light'"
            >
              {{ req.requirement.name }}
            </span>
          </p>
          <button
            class="button is-small is-primary mt-2"
            :disabled="!canStartSkill(skill) || startingSkillId === skill.id"
            :class="{ 'is-loading': startingSkillId === skill.id }"
            @click="startLearning(skill)"
          >
            Начать обучение
          </button>
          <p v-if="activeLearning" class="help is-danger mt-1">
            Сначала завершите текущее обучение.
          </p>
          <p v-else-if="!prerequisitesMet(skill)" class="help is-danger mt-1">
            Не выполнены требования.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import UserService from '../services/UserService'

export default {
  data () {
    return {
      learnings: [],
      availableSkills: [],
      learnedSkillIds: [],
      loading: false,
      startingSkillId: null,
      now: Math.floor(Date.now() / 1000),
      tickTimer: null,
    }
  },
  computed: {
    activeLearning () {
      return this.learnings.find(l => l.status === 'active') || null
    },
    localCompletionRate () {
      const learning = this.activeLearning
      if (!learning) return 0

      const duration = learning.skill.duration
      if (!duration) return 100

      const endAt = this.parseTimestamp(learning.created_at) + duration
      const remaining = endAt - this.now
      const rate = 100 - (remaining * 100) / duration

      return Math.min(100, Math.max(0, Math.round(rate)))
    },
    remainingSeconds () {
      const learning = this.activeLearning
      if (!learning) return 0

      const endAt = this.parseTimestamp(learning.created_at) + learning.skill.duration
      return Math.max(0, endAt - this.now)
    },
  },
  mounted () {
    this.loadData()
    this.tickTimer = setInterval(() => {
      this.now = Math.floor(Date.now() / 1000)
    }, 1000)
    this.$root.$on('learning-finished', this.loadData)
  },
  beforeDestroy () {
    if (this.tickTimer) {
      clearInterval(this.tickTimer)
    }
    this.$root.$off('learning-finished', this.loadData)
  },
  methods: {
    parseTimestamp (value) {
      return Math.floor(new Date(value).getTime() / 1000)
    },
    formatDuration (seconds) {
      if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60)
        const rest = seconds % 60
        return rest > 0 ? `${minutes} мин ${rest} сек` : `${minutes} мин`
      }
      return `${seconds} сек`
    },
    formatRemaining (seconds) {
      if (seconds <= 0) return 'завершается…'

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const rest = seconds % 60

      if (hours > 0) return `${hours} ч ${minutes} мин ${rest} сек`
      if (minutes > 0) return `${minutes} мин ${rest} сек`
      return `${rest} сек`
    },
    isRequirementMet (requirement) {
      return this.learnedSkillIds.includes(requirement.requirement.id)
    },
    prerequisitesMet (skill) {
      if (!skill.skill_requirements || skill.skill_requirements.length === 0) return true

      return skill.skill_requirements.every(req => this.isRequirementMet(req))
    },
    canStartSkill (skill) {
      return !this.activeLearning && this.prerequisitesMet(skill)
    },
    async loadData () {
      this.loading = true
      try {
        const [learningsResp, availableResp, skillsResp] = await Promise.all([
          UserService.getLearning(),
          UserService.getAvailableLearning(),
          UserService.getSkills(),
        ])
        this.learnings = learningsResp.data || []
        this.availableSkills = availableResp.data || []
        this.learnedSkillIds = (skillsResp.data || []).map(us => us.skill.id)
      } finally {
        this.loading = false
      }
    },
    async startLearning (skill) {
      if (!this.canStartSkill(skill)) return

      this.startingSkillId = skill.id
      try {
        const resp = await UserService.createLearning(skill.id)
        if (resp.data) {
          this.$buefy.toast.open({
            message: `Начато обучение: «${skill.name}»`,
            type: 'is-info',
            duration: 3000,
          })
          await this.loadData()
        }
      } catch (error) {
        const message = error.response?.data?.errors?.[0]?.full_message ||
          'Не удалось начать обучение'
        this.$buefy.toast.open({
          message,
          type: 'is-danger',
          duration: 5000,
        })
      } finally {
        this.startingSkillId = null
      }
    },
  },
}
</script>

<style scoped>
.learning-root {
  max-width: 640px;
  margin: 0 auto;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-4 {
  margin-top: 1.5rem;
}
</style>
